import { Link } from "react-router";
import { listCollection, readJson } from "../lib/content.server.js";
import { breadcrumbJsonLd, buildMeta } from "../lib/seo.js";
import { SITE } from "../lib/site.js";
import Breadcrumbs from "../components/Breadcrumbs/Breadcrumbs.jsx";
import Picture from "../components/Picture/Picture.jsx";
import SectionHeader from "../components/SectionHeader/SectionHeader.jsx";
import StateMap from "../components/StateMap/StateMap.jsx";
import PersonCard from "../components/PersonCard/PersonCard.jsx";
import CTABand from "../components/CTABand/CTABand.jsx";
import { surfaceFor } from "../lib/surface.js";
import styles from "./about.module.css";

const PAGE = {
  title: "About RDV Systems",
  description:
    "Since 2005, RDV Systems has helped DOTs and engineering firms visualize, analyze and communicate infrastructure designs. Publish, don't render.",
  path: "/about",
};
const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "About", path: PAGE.path },
];

export function meta() {
  return buildMeta({ ...PAGE, jsonLd: [breadcrumbJsonLd(CRUMBS)] });
}

export async function loader() {
  const team = listCollection("team");
  return {
    clients: readJson("clients"),
    leadership: team
      .filter((p) => p.group.includes("leadership"))
      .sort((a, b) => (a.order || 99) - (b.order || 99)),
  };
}

// The one switch for this page's light sections: these sit on the light "plan sheet"
// surface from app/styles/surfaces.css. Empty the list to put the whole page back on the
// dark surface; nothing else needs to change.
const LIGHT_SECTIONS = ["beliefs", "leadership"];
const surface = surfaceFor(LIGHT_SECTIONS);

const BELIEFS = [
  {
    title: "Cloud first",
    text: "A model that lives in the cloud is one everybody can open, on any device, with nothing to download.",
  },
  {
    title: "Remote collaboration",
    text: "Teams, agencies and the public review the same design at the same time, from wherever they are.",
  },
  {
    title: "Streamlined workflow",
    text: "Publish once; every image, tour and video comes from the same current model instead of another render job.",
  },
  {
    title: "Safety without waiting",
    text: "Roads should be audited for hazards before the first crash, not ranked by the crashes they already had.",
  },
];

export default function About({ loaderData }) {
  const { clients, leadership } = loaderData;
  const dots = clients.filter((c) => c.type === "dot").length;
  const states = new Set(clients.map((c) => c.state).filter(Boolean)).size;
  const [us, il] = SITE.offices;
  return (
    <>
      <section className={styles.hero} aria-labelledby="about-title">
        <div className={`container ${styles.heroGrid}`}>
          <div className={styles.heroText}>
            <Breadcrumbs items={CRUMBS} />
            <h1 id="about-title" className={styles.heroTitle}>
              Visualization and road safety for DOTs, since {SITE.foundedYear}
            </h1>
            <p className={styles.heroLead}>
              RDV Systems builds 3D Visualization and Road Safety Audit 3D for state DOTs,
              engineering firms and public agencies, from {us.city}, {us.region} and {il.city},{" "}
              {il.country}.
            </p>
            {/* Round figures from sales (2026-09-29), so the page does not need updating with
                every new client. clients.json lists only the clients with logos. */}
            <ul className={styles.figures} aria-label="RDV Systems in figures">
              <li>
                <strong>20+</strong> state DOTs
              </li>
              <li>
                <strong>20+</strong> local and regional agencies
              </li>
              <li>
                <strong>75+</strong> consulting firms
              </li>
            </ul>
          </div>
          <Picture
            src="/images/site/about/road-portrait"
            alt="A road curving through a forest, from above"
            ratio="4 / 3"
            widths={[480, 960, 1600]}
            fallbackWidth={960}
            sizes="(min-width: 64rem) 50vw, 100vw"
            priority
            className={styles.heroImage}
          />
        </div>
      </section>

      <section className="section" aria-labelledby="story-title">
        <div className={`container ${styles.story}`}>
          <Picture
            src="/images/site/hero/ddi"
            alt="A diverging diamond interchange, published as a 3D model"
            widths={[480, 960, 1600]}
            fallbackWidth={960}
            sizes="(min-width: 64rem) 50vw, 100vw"
            className={styles.storyImage}
          />
          <div className={styles.storyText}>
            <h2 id="story-title" className={styles.storyTitle}>
              Publish, don&apos;t render
            </h2>
            <p>
              Visualization and safety analysis of infrastructure used to be slow and cumbersome.
              RDV Systems was founded in {SITE.foundedYear} to change that: first with 3D models
              published for everyone to open instead of rendered frame by frame, then with a road
              safety audit that runs on the 3D model itself.
            </p>
            <dl className={styles.mission}>
              <div>
                <dt>Mission</dt>
                <dd>
                  To lead the industry with a streamlined 3D real-time, visual communication
                  solution.
                </dd>
              </div>
              <div>
                <dt>Vision</dt>
                <dd>
                  A globally cost-effective tool for project visualization and road safety that
                  dramatically expands cooperation and communication for teams and the public.
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </section>

      <section
        className="section"
        data-surface={surface("beliefs")}
        aria-labelledby="beliefs-title"
      >
        <div className="container">
          <SectionHeader id="beliefs-title" title="What we build on" />
          <ul className={styles.beliefs}>
            {BELIEFS.map((b) => (
              <li key={b.title}>
                <h3>{b.title}</h3>
                <p>{b.text}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="section" aria-labelledby="where-title">
        <div className={`container ${styles.where}`}>
          <div className={styles.whereText}>
            <h2 id="where-title">Where our clients are</h2>
            <p>
              {dots} state DOTs, and the cities, agencies and engineering firms around them, in{" "}
              {states} states.
            </p>
            <Link to="/about/clients" className={styles.whereLink}>
              See every client
            </Link>
          </div>
          <StateMap clients={clients} />
        </div>
      </section>

      {leadership.length ? (
        <section
          className="section"
          data-surface={surface("leadership")}
          aria-labelledby="leadership-title"
        >
          <div className="container">
            <SectionHeader
              id="leadership-title"
              title="Leadership"
              link={{ to: "/about/team", label: "Meet the whole team" }}
            />
            <div className={styles.leaders}>
              {leadership.map((p) => (
                <PersonCard key={p.slug} person={p} size="lead" />
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <CTABand
        variant="statement"
        image="/images/site/hero/henderson"
        title="Work with us"
        text="See what Road Safety Audit 3D and 3D Visualization would do on your project."
        to="/contact"
        label="Schedule a consultation"
        secondary={{ to: "/demo", label: "Request a demo" }}
      />
    </>
  );
}
